import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faPlus, faEdit } from '@fortawesome/free-solid-svg-icons';
import BaseURL from '../assets/contants/BaseURL';
import Header from '../components/header';

const EnergySourcedata = () => {
    const [energySources, setEnergySources] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [energyTypes, setEnergyTypes] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
    const [currentEditId, setCurrentEditId] = useState(null);
    const [formData, setFormData] = useState({
        date: '',
        department: '',
        energy_type: '',
        value: ''
    });
    const itemsPerPage = 10;
    const modalRef = useRef(null);
    const deleteModalRef = useRef(null);
    const [checkedItems, setCheckedItems] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState('');
    const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        const superuserStatus = sessionStorage.getItem('superuser') === 'true';
        setIsSuperuser(superuserStatus);
    }, []);

    const fetchEnergySources = async (fromDateParam = null, toDateParam = null) => {
        try {
            const params = {};
            if (fromDateParam) params.from_date = fromDateParam;
            if (toDateParam) params.to_date = toDateParam;
            
            const response = await axios.get(`${BaseURL}meters/energysource/`, {
                params: params
            });
            console.log(response.data)
            
            const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
            setEnergySources(sortedData);
        } catch (err) {
            console.log(err.message);
        }
    };

    useEffect(() => {
        fetchEnergySources(fromDate, toDate);
    }, [fromDate, toDate]);

    const handleClearDates = () => {
        setFromDate("");
        setToDate("");
        fetchEnergySources();
        setCurrentPage(1);
    };

    const filterEnergySources = (sources) => {
        if (!searchTerm) return sources;
        
        return sources.filter(source => {
            const departmentName = getDepartmentName(source.department).toLowerCase();
            const energyTypeName = getEnergyTypeName(source.energy_type).toLowerCase();
            const date = source.date.toLowerCase();
            const value = source.value.toString().toLowerCase();
            const search = searchTerm.toLowerCase();
            
            return (
                departmentName.includes(search) ||
                energyTypeName.includes(search) ||
                date.includes(search) ||
                value.includes(search)
            );
        });
    };

    const getDepartmentName = (departmentId) => {
        const department = departments.find(dept => dept.id === departmentId);
        return department ? department.machine_name : 'Unknown';
    };

    const getEnergyTypeName = (energyTypeId) => {
        const energyType = energyTypes.find(type => type.id === energyTypeId);
        return energyType ? `${energyType.name} (${energyType.unit})` : 'Unknown';
    };

    const filteredData = filterEnergySources(energySources);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        const token = sessionStorage.getItem('token');
        const fetchDepartments = async () => {
            try {
                const response = await axios.get(`${BaseURL}meters/enpi-machine/`,
                    {
                    headers: {
                        Authorization: `Token ${token}`,
                    },
                    }
                );
                setDepartments(response.data);
            } catch (err) {
                console.log(err.message);
            }
        };

        fetchDepartments();
    }, []);

    useEffect(() => {
        const fetchEnergyTypes = async () => {
            try {
                const response = await axios.get(`${BaseURL}meters/energy-type/`);
                setEnergyTypes(response.data);
            } catch (err) {
                console.log(err.message);
            }
        };

        fetchEnergyTypes();
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const openAddModal = () => {
        setIsAddModalVisible(true);
    };

    const closeAddModal = () => {
        setIsAddModalVisible(false);
        setFormData({
            date: '',
            department: '',
            energy_type: '',
            value: ''
        });
    };

    const openEditModal = (source) => {
        setCurrentEditId(source.id);
        setFormData({
            date: source.date,
            department: source.department,
            energy_type: source.energy_type,
            value: source.value
        });

        const selectedEnergy = energyTypes.find(type => type.id === source.energy_type);
        setSelectedUnit(selectedEnergy ? selectedEnergy.unit : '');
        
        setIsEditModalVisible(true);
    };

    const closeEditModal = () => {
        setIsEditModalVisible(false);
        setCurrentEditId(null);
        setFormData({
            date: '',
            department: '',
            energy_type: '',
            value: ''
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'energy_type') {
            const selectedEnergy = energyTypes.find(type => type.id === parseInt(value));
            setSelectedUnit(selectedEnergy ? selectedEnergy.unit : '');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${BaseURL}meters/energysource/`, formData);
            setEnergySources(prev => [response.data, ...prev]);
            setSuccessMessage('Energy source added successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            closeAddModal();
            await fetchEnergySources(fromDate, toDate);
        } catch (err) {
            console.error('Error adding energy source:', err);
            setSuccessMessage('Failed to add energy source');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(`${BaseURL}meters/energysource/${currentEditId}/`, formData);
            setEnergySources(prev => 
                prev.map(item => item.id === currentEditId ? response.data : item)
            );
            setSuccessMessage('Energy source updated successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            closeEditModal();
            await fetchEnergySources(fromDate, toDate);
        } catch (err) {
            console.error('Error updating energy source:', err);
            setSuccessMessage('Failed to update energy source');
            setTimeout(() => setSuccessMessage(''), 3000);
        }
    };

    const handleCheckboxChange = (id) => {
        setCheckedItems((prev) => ({
        ...prev,
        [id]: !prev[id],
        }));
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        const newCheckedItems = {};
        energySources.forEach(item => {
        newCheckedItems[item.id] = isChecked;
        });
        setCheckedItems(newCheckedItems);
    };

    const handleDeleteEnergySource = async () => {
        try {
          await Promise.all(
            Object.keys(checkedItems)
              .filter((id) => checkedItems[id])
              .map((id) =>
                axios.delete(`${BaseURL}meters/energysource/${id}/`)
              )
          );
          setCheckedItems({});
          setSuccessMessage('Selected meters deleted successfully!');
          setTimeout(() => setSuccessMessage(''), 3000);
          await fetchEnergySources(fromDate, toDate);
        } catch (error) {
          console.error("Error deleting meters:", error);
        } finally {
          setDeleteModalOpen(false);
        }
    };

    const lightenColor = (color, percent) => {
        if (!color || color === '#FFFFFF' || color.length !== 7) return '#f0f0f0';
        
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        
        const R = Math.min(255, ((num >> 16) + amt));
        const G = Math.min(255, (((num >> 8) & 0x00FF) + amt));
        const B = Math.min(255, ((num & 0x0000FF) + amt));
        
        return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
    };

    return (
        <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{
                backgroundColor: '#007acc',
                color: 'white',
                alignContent: 'center',
                fontSize: '14px',
                height: '40px'
            }}>
                <Header title="ENERGY SOURCE DATA" />
            </div>

            <div style={{
                border: '1px solid #ccc',
                padding: '10px',
                marginTop: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                margin: '20px auto',
                maxWidth: '1000px',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '60%' }}>
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                flex: '1',
                                marginRight: '10px',
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{ color: '#007acc', cursor: 'pointer', marginRight: '10px' }}
                        />
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            style={{
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                marginRight: '10px',
                            }}
                            />
                            <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            style={{
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                marginRight: '10px',
                            }}
                            />
                            {(fromDate || toDate) && (
                            <button
                                onClick={handleClearDates}
                                style={{
                                padding: '10px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                }}
                            >
                                Clear Dates
                            </button>
                            )}
                    </div>

                    <div style={{ marginLeft: 'auto' }}>
                        {isSuperuser && (
                        <button
                            style={{
                                padding: '10px 15px',
                                marginRight: '10px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                const selectedSource = Object.keys(checkedItems).filter((id) => checkedItems[id]);
                                if (selectedSource.length > 0) {
                                setDeleteModalOpen(true);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} style={{ marginRight: '5px' }} />
                            Delete
                        </button>
                        )}
                        <button
                            onClick={openAddModal}
                            style={{
                                padding: '10px 15px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                marginRight: '10px',
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
                            Add
                        </button>
                    </div>
                </div>
            </div>
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
                {successMessage && (
                    <div style={{
                        padding: '10px',
                        borderRadius: '5px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        margin: '10px auto',
                        width: 'fit-content',
                    }}>
                        {successMessage}
                    </div>
                )}
            </div>

            <div style={{
                marginTop: '20px',
                padding: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                maxWidth: '1000px',
                margin: '20px auto',
                marginBottom: '70px',
            }}>
                <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#007acc', color: 'white' }}>
                        <tr>
                            <th style={{ padding: '10px' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        Object.keys(checkedItems).length === paginatedData.length &&
                                        Object.values(checkedItems).every(value => value)
                                    }
                                />
                            </th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>S.No</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Date</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Department</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Energy Type</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Value</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>kWh</th>
                            <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            paginatedData.map((source, index) => (
                                <tr key={source.id} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : '#ffffff' }}>
                                    <td style={{ padding: '10px' }}>
                                        <input
                                            type="checkbox"
                                            checked={checkedItems[source.id] || false}
                                            onChange={() => handleCheckboxChange(source.id)}
                                        />
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{source.date}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                        {(() => {
                                            const department = departments.find(dept => dept.id === source.department);
                                            const originalColor = department?.color || '#ff5252';
                                            const lightenedColor = lightenColor(originalColor, 25);
                                            
                                            return (
                                                <span style={{
                                                    backgroundColor: lightenedColor,
                                                    color: '#000000',
                                                    fontSize: '14px',
                                                    border: `2px solid ${originalColor}`,
                                                    borderRadius: '15px',
                                                    padding: '0px 8px',
                                                    display: 'inline-block',
                                                    flex: 1,
                                                    marginRight: '5px',
                                                    textAlign: 'center'
                                                }}>
                                                    {department ? department.machine_name : 'Unknown'}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                        {getEnergyTypeName(source.energy_type)}
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>{source.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                        {(() => {
                                            const energyType = energyTypes.find(type => type.id === source.energy_type);
                                            if (!energyType) return 'N/A';
                                            
                                            if (energyType.name.toLowerCase().includes('lpg')) {
                                                return source.lpg_kWh ? source.lpg_kWh.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2}) : (source.value * 14.4).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2});
                                            } else if (energyType.name.toLowerCase().includes('diesel')) {
                                                return source.diesel_kWh ? source.diesel_kWh.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2}) : (source.value * 3.3).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2});
                                            }
                                            return 'N/A';
                                        })()}
                                    </td>
                                    <td style={{ padding: '10px', color: 'blue', cursor: 'pointer', textAlign: 'center' }}>
                                        <FontAwesomeIcon icon={faEdit} onClick={() => openEditModal(source)}/>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            margin: '0 5px',
                            padding: '5px 10px',
                            background: '#007acc',
                            color: currentPage === 1 ? '#9e9e9e' : '#ffffff',
                            border: 'none',
                            borderRadius: '25px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            boxShadow: currentPage === 1 ? 'none' : '0px 4px 8px rgba(0, 0, 0, 0.2)',
                            fontSize: '12px',
                        }}
                    >
                        Previous
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => (
                        <button
                            key={i}
                            onClick={() => handlePageChange(i + 1)}
                            style={{
                                margin: '0 5px',
                                padding: '5px 10px',
                                background: currentPage === i + 1 ? '#007acc' : 'linear-gradient(to right, #f7f7f7, #e0e0e0)',
                                color: currentPage === i + 1 ? '#ffffff' : '#333333',
                                border: '1px solid #ddd',
                                borderRadius: '25px',
                                cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                            margin: '0 5px',
                            padding: '5px 10px',
                            background: '#007acc',
                            color: currentPage === totalPages ? '#9e9e9e' : '#ffffff',
                            border: 'none',
                            borderRadius: '25px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            boxShadow: currentPage === totalPages ? 'none' : '0px 4px 8px rgba(0, 0, 0, 0.2)',
                            fontSize: '12px',
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>

            {isAddModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 999,
                }}>
                    <div ref={modalRef} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        width: '650px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}>
                        <h3 style={{
                            fontSize: '24px',
                            color: '#333',
                            marginBottom: '20px',
                            textAlign: 'center',
                        }}>
                            Add New Energy Source Data
                        </h3>
                        <form onSubmit={handleSubmit}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '20px',
                                marginBottom: '20px',
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '90%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Department</label>
                                    <select
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '93%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map((dept) => (
                                            <option key={dept.id} value={dept.id}>
                                                {dept.machine_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Energy Type</label>
                                    <select
                                        name="energy_type"
                                        value={formData.energy_type}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '95%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <option value="">Select Energy Type</option>
                                        {energyTypes.map((energy) => (
                                            <option key={energy.id} value={energy.id}>
                                                {energy.name} ({energy.unit})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Value in {selectedUnit && `(${selectedUnit})`}</label>
                                    <input
                                        type="number"
                                        name="value"
                                        value={formData.value}
                                        onChange={handleChange}
                                        required
                                        step="0.01"
                                        style={{
                                            width: '90%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '20px',
                            }}>
                                <button
                                    type="submit"
                                    style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '120px',
                                    }}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={closeAddModal}
                                    style={{
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '120px',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isEditModalVisible && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 999,
                }}>
                    <div ref={modalRef} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        width: '650px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}>
                        <h3 style={{
                            fontSize: '24px',
                            color: '#333',
                            marginBottom: '20px',
                            textAlign: 'center',
                        }}>
                            Edit Energy Source Data
                        </h3>
                        <form onSubmit={handleEditSubmit}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '20px',
                                marginBottom: '20px',
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '90%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Department</label>
                                    <select
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '93%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map((dept) => (
                                            <option key={dept.id} value={dept.id}>
                                                {dept.machine_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Energy Type</label>
                                    <select
                                        name="energy_type"
                                        value={formData.energy_type}
                                        onChange={handleChange}
                                        required
                                        style={{
                                            width: '95%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    >
                                        <option value="">Select Energy Type</option>
                                        {energyTypes.map((energy) => (
                                            <option key={energy.id} value={energy.id}>
                                                {energy.name} ({energy.unit})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontWeight: '600',
                                        color: '#444',
                                        marginBottom: '8px',
                                    }}>Value in {selectedUnit && `(${selectedUnit})`}</label>
                                    <input
                                        type="number"
                                        name="value"
                                        value={formData.value}
                                        onChange={handleChange}
                                        required
                                        step="0.01"
                                        style={{
                                            width: '90%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #ddd',
                                            fontSize: '16px',
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '20px',
                            }}>
                                <button
                                    type="submit"
                                    style={{
                                        backgroundColor: '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '120px',
                                    }}
                                >
                                    Update
                                </button>
                                <button
                                    type="button"
                                    onClick={closeEditModal}
                                    style={{
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        padding: '12px 25px',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        width: '120px',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {isDeleteModalOpen && (
                <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                }}
                >
                <div
                    ref={deleteModalRef}
                    style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "10px",
                    width: "400px",
                    textAlign: "center",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    }}
                >
                    <h3>Confirm Deletion</h3>
                    <p>Are you sure you want to delete the selected Energy Source Data?</p>
                    <div
                    style={{
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-around",
                    }}
                    >
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                        style={{
                            padding: "10px 15px",
                            color: "white",
                            backgroundColor: "#4CAF50",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                        onClick={handleDeleteEnergySource}
                        >
                        Yes, Delete
                        </button>
                        <button
                        style={{
                            padding: "10px 15px",
                            color: "white",
                            backgroundColor: "#f44336",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                        onClick={() => setDeleteModalOpen(false)}
                        >
                        Cancel
                        </button>
                    </div>

                    </div>
                </div>
                </div>
            )}
        </div>
    );
};

export default EnergySourcedata;